/**
 * Contract Detail Component
 * Displays contract information and manages sessions
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  LinearProgress,
  Alert,
  IconButton,
  Collapse,
  Paper,
  List,
  ListItem,
  ListItemText,
  Grow,
  Fade,
  AlertTitle,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  DialogActions,
  TextField,
  Snackbar,
  AlertColor,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Schedule as ScheduleIcon,
  Draw as DrawIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
  Note as NoteIcon,
  Message as MessageIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  Send as SendIcon,
  Email as EmailIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import {
  Contract,
  Session,
  Note,
  SessionStatus,
  LocationType,
} from '@/types/contracts'
import { ContractSessionApiModel } from '@/services/contractsService'
import { SessionRow } from './index'
import EvvModal from './EvvModal'
import NoteEditor from './NoteEditor'
import PaymentCompletionDialog from './PaymentCompletionDialog'
import ClaimPreviewDialog from './ClaimPreviewDialog'
import ContractTimeline from './ContractTimeline'
import DocumentsSection from './DocumentsSection'
import ContractDocuments from './ContractDocuments'
import PoCGenerationDialog from './PoCGenerationDialog'
import PoCDocumentsDialog, { PoCDocument } from './PoCDocumentsDialog'
import PoCDetailModal from './PoCDetailModal'
import PreauthSubmissionDialog from './PreauthSubmissionDialog'
import PreauthCompletedDialog from './PreauthCompletedDialog'
import PreauthDeclinedDialog from './PreauthDeclinedDialog'
import { pocApi, PoCDocument as ApiPoCDocument } from '@/services/pocApi'
import { notesApi } from '@/services/notesApi'
import {
  ContractTimelineItem,
  ContractDocument,
  getContractSessions,
} from '@/services/contractsService'
import { completeSessionPayment } from '@/services/mockContractsApi'
import api from '@/services/api'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChatDialog } from '@/chat/components/ChatDialog'
import { useAppSelector } from '@/store'
import {
  createConversationPatientAgency,
  createConversationTherapistAgency,
  resolveTwilioConversationSid,
} from '@/chat/services/chatApi'
import type { ChatConversation } from '@/chat/services/chatApi'
import {
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiCheckCircle,
} from 'react-icons/fi'
import toast from 'react-hot-toast'
import { AreYouSurePopup } from '../Popup'

interface ContractDetailProps {
  contract: Contract
  onBack: () => void
  onUpdateSession: (session: Session) => void
  onUpdateNote: (note: Note) => void
  onGenerateClaim?: () => void
  onSignContract?: (contractId: string) => void
  onRefreshContract?: () => Promise<void> | void
  onShowSnackbar?: (message: string, severity: AlertColor) => void
  timeline?: ContractTimelineItem[]
  timelineLoading?: boolean
  documents?: Array<{
    id: string
    type: string
    createdOn: string
    status?: 'pending' | 'authorized' | 'rejected'
    downloadUrl?: string
  }>
  documentsLoading?: boolean
  contractDocuments?: ContractDocument[]
  contractDocumentsLoading?: boolean
  onDownloadContractDocument?: (documentId: string) => Promise<string | void>
  onDownloadDocument?: (documentId: string) => Promise<void>
  onUploadDocument?: () => void
  contractAmount?: number
  nextSessionDateprop?: Date | null
  sessionLength?: number | null
  sessionData?: any[]

}


const ContractDetail: React.FC<ContractDetailProps> = ({
  contract,
  onBack,
  onUpdateSession,
  onUpdateNote,
  onGenerateClaim,
  onSignContract,
  onRefreshContract,
  onShowSnackbar: onShowSnackbarProp,
  timeline = [],
  timelineLoading = false,
  documents = [],
  documentsLoading = false,
  contractDocuments = [],
  contractDocumentsLoading = false,
  onDownloadContractDocument,
  onDownloadDocument,
  onUploadDocument,
  contractAmount,
  nextSessionDateprop,
  sessionLength,
  sessionData,
}) => {

  const navigate = useNavigate()
  const [showInitialEval, setShowInitialEval] = useState(false)
  const [showGoals, setShowGoals] = useState(false)
  const [evvModalOpen, setEvvModalOpen] = useState(false)
  const [evvMode, setEvvMode] = useState<'check-in' | 'check-out'>('check-in')
  const [selectedSession, setSelectedSession] = useState<Session | null>(null)
  const [noteEditorOpen, setNoteEditorOpen] = useState(false)
  const [editingNote, setEditingNote] = useState<Note | undefined>(undefined)
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)
  const [claimPreviewDialogOpen, setClaimPreviewDialogOpen] = useState(false)
  const [isClaimSubmitMode, setIsClaimSubmitMode] = useState(false)
  const [completingSession, setCompletingSession] = useState<Session | null>(
    null
  )
  const [pendingSessionIdForCompletion, setPendingSessionIdForCompletion] =
    useState<string | null>(null)
  const [processingPayment, setProcessingPayment] = useState(false)
  const [signingModalOpen, setSigningModalOpen] = useState(false)
  const [signingUrl, setSigningUrl] = useState<string | null>(null)
  const [signingLoading, setSigningLoading] = useState(false)
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelLoading, setCancelLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: AlertColor
  }>({
    open: false,
    message: '',
    severity: 'info',
  })



  // Track iframe loads - first load is signing page, second load is completion page
  const iframeLoadCountRef = useRef(0)
  const hasSubmittedRef = useRef(false)

  // State for Add New Session dialog
  const [addSessionDialogOpen, setAddSessionDialogOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [slotsForDate, setSlotsForDate] = useState<any[]>([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [selectedSlots, setSelectedSlots] = useState<
    Record<string, { slotId: string; startTime: string; endTime: string }>
  >({})
  const [paymentMethod, setPaymentMethod] = useState<
    'insurance' | 'out_of_pocket' | ''
  >('')
  const [isSubmittingSession, setIsSubmittingSession] = useState(false)
  const [monthOffset, setMonthOffset] = useState(0)

  // State for Reschedule Session dialog
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false)
  const [selectedSessionForReschedule, setSelectedSessionForReschedule] =
    useState<Session | null>(null)
  const [rescheduleDate, setRescheduleDate] = useState<Date | null>(null)
  const [rescheduleSlots, setRescheduleSlots] = useState<any[]>([])
  const [loadingRescheduleSlots, setLoadingRescheduleSlots] = useState(false)
  const [selectedRescheduleSlot, setSelectedRescheduleSlot] =
    useState<any>(null)
  const [isSubmittingReschedule, setIsSubmittingReschedule] = useState(false)
  const [rescheduleMonthOffset, setRescheduleMonthOffset] = useState(0)

  // State for Cancel Session dialog
  const [cancelSessionDialogOpen, setCancelSessionDialogOpen] = useState(false)
  const [selectedSessionForCancel, setSelectedSessionForCancel] =
    useState<Session | null>(null)
  const [cancelSessionReason, setCancelSessionReason] = useState('')
  const [isSubmittingCancel, setIsSubmittingCancel] = useState(false)

  // State for Chat Dialog
  const [chatDialogOpen, setChatDialogOpen] = useState(false)
  const [messageMenuAnchor, setMessageMenuAnchor] = useState<null | HTMLElement>(null)
  const [messageTarget, setMessageTarget] = useState<'patient' | 'provider' | null>(null)
  const [agencyConversation, setAgencyConversation] = useState<ChatConversation | null>(null)
  const [agencyConversationLoading, setAgencyConversationLoading] = useState(false)
  const [agencyConversationError, setAgencyConversationError] = useState<string | null>(null)

  // State for Plan of Care Dialog
  const [pocDialogOpen, setPocDialogOpen] = useState(false)
  const [selectedSessionForPoC, setSelectedSessionForPoC] =
    useState<Session | null>(null)
  const [editingPoC, setEditingPoC] = useState<ApiPoCDocument | null>(null)

  // State for PoC Documents Dialog
  const [pocDocumentsDialogOpen, setPocDocumentsDialogOpen] = useState(false)
  const [pocDocuments, setPocDocuments] = useState<PoCDocument[]>([])
  const [pocDocumentsLoading, setPocDocumentsLoading] = useState(false)
  const [selectedPoCForView, setSelectedPoCForView] =
    useState<ApiPoCDocument | null>(null)
  const [pocDetailModalOpen, setPocDetailModalOpen] = useState(false)

  // Preauthorization dialog states
  const [preauthSubmissionDialogOpen, setPreauthSubmissionDialogOpen] =
    useState(false)
  const [preauthCompletedDialogOpen, setPreauthCompletedDialogOpen] =
    useState(false)
  const [preauthDeclinedDialogOpen, setPreauthDeclinedDialogOpen] =
    useState(false)
  const [selectedPoCForPreauth, setSelectedPoCForPreauth] =
    useState<ApiPoCDocument | null>(null)
  const [preauthLoading, setPreauthLoading] = useState(false)
  // State for Complete Contract dialog
  const [completeContractDialogOpen, setCompleteContractDialogOpen] =
    useState(false)
  const [completingContract, setCompletingContract] = useState(false)
  const [areYouSureDialogOpen, setAreYouSureDialogOpen] = useState(false)

  const showSnackbar = (message: any, severity: AlertColor = 'info') => {
    const safeMessage = typeof message === 'string'
      ? message
      : message?.message || message?.mesgid || (typeof message === 'object' ? 'An unexpected error occurred' : String(message));

    // Use parent snackbar if available (persists across re-renders), otherwise use local
    if (onShowSnackbarProp) {
      onShowSnackbarProp(safeMessage, severity)
    } else {
      setSnackbar({ open: true, message: safeMessage, severity })
    }
  }

  console.log("contract",contract)

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }))
  }

  const progress =
    contract.plannedSessions > 0
      ? (contract.completedSessions / contract.plannedSessions) * 100
      : 0
  const hasNoSessions = !contract.sessions?.length
  const claimableSessions = contract.sessions.filter(s => s.claimable)
  const isPendingContract = contract.status === 'pending'
  const isCancelledContract =
    contract.status === 'cancelled' || contract.isCancelled === true
  const isCompletedContract = contract.status === 'completed'
  const isActiveContract = contract.status === 'active'
  const needsTherapistSignature = isPendingContract && !contract.therapistSigned

  const handleStartEVV = (session: Session, mode: 'check-in' | 'check-out') => {
    if (isCancelledContract) return
    // Both check-in and check-out are handled by their respective dialogs
    // Just refresh the contract to sync with server
    if (onRefreshContract) {
      onRefreshContract()
    }
  }

  const handleOpenNote = (session: Session, note?: Note) => {
    // Allow opening notes for cancelled contracts but only in read-only mode
    setSelectedSession(session)
    setEditingNote(note)
    setNoteEditorOpen(true)
  }

  const handleEVVSuccess = (updatedSession: Session) => {
    onUpdateSession(updatedSession)
    setEvvModalOpen(false)
  }

  const handleNoteSuccess = async (note: Note) => {
    onUpdateNote(note)
    setNoteEditorOpen(false)

    // Handle note deletion - remove from session notes immediately
    if ((note as any).isDeleted && selectedSession) {
      const noteId = note._id || (note as any).id
      const updatedNotes = selectedSession.notes.filter((n: any) => {
        const nId = n._id || n.id
        return nId !== noteId
      })

      const updatedSession: Session = {
        ...selectedSession,
        notes: updatedNotes,
      }

      if (onUpdateSession) {
        onUpdateSession(updatedSession)
      }
      return
    }

    // Update the session's notes array to reflect changes immediately
    if (selectedSession) {
      try {
        const contractId = contract.id
        const sessionId = selectedSession._id || selectedSession.id

        // Fetch updated notes for the session
        const updatedNotes = await notesApi.getNotesByContractAndSession(
          contractId,
          sessionId
        )

        // Convert API notes to the format expected by SessionRow
        const convertedNotes = updatedNotes.map(n => ({
          _id: n._id,
          id: n._id,
          sessionId: n.sessionId,
          content: n.content,
          status: n.status,
          visibleto_patient: n.visibleto_patient,
          noteCategory: n.noteCategory,
          createdAt: n.createdAt,
          updatedAt: n.updatedAt,
          signature: n.signature,
        }))

        // Update the session with new notes
        const updatedSession: Session = {
          ...selectedSession,
          notes: convertedNotes as any,
        }

        // Notify parent to update the session
        if (onUpdateSession) {
          onUpdateSession(updatedSession)
        }
      } catch (error) {
        console.error('Failed to refresh session notes:', error)
        // Don't show error to user, just log it
        // The note was saved successfully, it will appear on next page reload
      }
    }
  }

  const handleRaiseInvoice = (sessionId: string, amount: number) => {
    // In a real app, this would call an API to generate an invoice
    console.log(`Raising invoice for session ${sessionId} - Amount: $${amount}`)
    // showSnackbar(
    //   `Invoice generation initiated for session ${sessionId}\nAmount: $${amount.toFixed(2)}\n\nIn a production environment, this would:\n1. Generate an invoice PDF\n2. Send it to the patient's email\n3. Update the session payment status\n4. Create an entry in the billing system`
    // )
    // Here you would typically call onUpdateSession with updated payment info
  }

  const handleCompleteSession = async (sessionId: string) => {
    if (isCancelledContract) return
    // Set pending session ID to trigger dialog after contract refresh
    setPendingSessionIdForCompletion(sessionId)

    try {
      // Fetch updated sessions using get-session API to get latest session status
      const sessionsResponse = await getContractSessions(contract.id)

      // Find the updated session from the response
      if (sessionsResponse?.sessions && sessionsResponse.sessions.length > 0) {
        const updatedSessionData = sessionsResponse.sessions.find(
          (s: any) => s.session_ids === sessionId || s._id === sessionId
        )

        // If we found the updated session, update it immediately
        if (updatedSessionData && onUpdateSession) {
          // Get contract info from the session's contract_id
          const contractInfo = updatedSessionData.contract_id

          // Build scheduledAt from session_date and session_slot
          let scheduledAt = new Date().toISOString()
          if (updatedSessionData.session_date) {
            const date = new Date(updatedSessionData.session_date)
            if (updatedSessionData.session_slot) {
              const [startPart] = updatedSessionData.session_slot.split('-')
              const start = startPart?.trim()
              if (start) {
                const slotMatch = start.match(
                  /(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i
                )
                if (slotMatch) {
                  let hours = parseInt(slotMatch[1], 10)
                  const minutes = parseInt(slotMatch[2] ?? '0', 10)
                  const meridiem = slotMatch[3]?.toUpperCase()
                  if (meridiem === 'PM' && hours < 12) hours += 12
                  if (meridiem === 'AM' && hours === 12) hours = 0
                  date.setHours(hours, minutes, 0, 0)
                }
              }
            }
            scheduledAt = date.toISOString()
          }

          // Build payment info
          const paymentType =
            contractInfo?.payment_method_type === 'insurance'
              ? 'insurance'
              : 'self-pay'
          const totalAmount =
            updatedSessionData.session_amount ??
            contractInfo?.total_amount ??
            updatedSessionData.service_id?.private_pay_rate ??
            0
          const rawPatientShare =
            paymentType === 'insurance'
              ? (updatedSessionData.co_pay ?? 0)
              : totalAmount - (updatedSessionData.approved_amount ?? 0)

          // Normalize status - map common status values
          let normalizedStatus: SessionStatus = 'completed'
          const statusStr = (
            updatedSessionData.status || 'completed'
          ).toLowerCase()
          if (statusStr.includes('complete')) normalizedStatus = 'completed'
          else if (statusStr.includes('clock') && statusStr.includes('out'))
            normalizedStatus = 'clocked-out'
          else if (statusStr.includes('progress'))
            normalizedStatus = 'in-progress'
          else if (statusStr.includes('schedule'))
            normalizedStatus = 'scheduled'
          else if (statusStr.includes('cancel')) normalizedStatus = 'cancelled'
          else if (statusStr.includes('miss')) normalizedStatus = 'missed'

          // Map location type
          const modality =
            updatedSessionData.service_id?.modality?.[0]?.toLowerCase()
          let locationType: LocationType = 'in-person'
          if (modality?.includes('home') || modality?.includes('in-home'))
            locationType = 'home'
          else if (modality?.includes('tele') || modality?.includes('virtual'))
            locationType = 'telehealth'

          // Build the updated session object
          const updatedSession: Session = {
            _id: updatedSessionData._id,
            id: updatedSessionData.session_ids || updatedSessionData._id,
            contractId: contractInfo?._id || contract.id,
            contract_id: updatedSessionData.contract_ids || '',
            scheduledAt,
            status: normalizedStatus,
            evv: updatedSessionData.evvs || {},
            notes: updatedSessionData.notes || [],
            durationMinutes:
              updatedSessionData.duration_mins ||
              updatedSessionData.service_id?.session_length,
            slotDurationMinutes: updatedSessionData.service_id?.session_length,
            claimable: paymentType === 'insurance',
            locationType,
            paymentInfo: {
              paymentType,
              totalAmount,
              insurancePaidAmount: updatedSessionData.approved_amount ?? 0,
              coPayAmount: updatedSessionData.co_pay ?? 0,
              patientOwedAmount: Math.max(0, rawPatientShare),
              paymentStatus: 'pending',
              claimStatus: 'pending',
            },
            session_slot: updatedSessionData.session_slot,
            session_order: updatedSessionData.session_order,
            isPocGenerated: updatedSessionData.isPocGenerated,
            payment_method: (updatedSessionData as any).payment_method,
          }

          // Update the session immediately
          onUpdateSession(updatedSession)
        }
      }

      // Also refresh the full contract data to ensure everything is in sync
      if (onRefreshContract) {
        await onRefreshContract()
      }
    } catch (error) {
      console.error('Error fetching updated sessions:', error)
      // Still refresh contract even if session fetch fails
      if (onRefreshContract) {
        await onRefreshContract()
      }
    }
  }

  // Effect to handle opening completion dialog after contract refresh
  useEffect(() => {
    if (pendingSessionIdForCompletion) {
      // Find the updated session after refresh
      const session = contract.sessions.find(
        s => s.id === pendingSessionIdForCompletion
      )
      if (session) {
        setCompletingSession(session)
        setPendingSessionIdForCompletion(null) // Clear pending ID

        // Show ClaimPreviewDialog for insurance, PaymentCompletionDialog for self-pay
        if (session.paymentInfo?.paymentType === 'insurance') {
          setIsClaimSubmitMode(true) // This is a new submission
          setClaimPreviewDialogOpen(true)
        } else {
          setPaymentDialogOpen(true)
        }
      }
    }
  }, [contract.sessions, pendingSessionIdForCompletion])

  const handleConfirmPaymentCompletion = async (sessionId: string) => {
    setProcessingPayment(true)
    try {
      const updatedSession = await completeSessionPayment(sessionId)
      onUpdateSession(updatedSession)
      setPaymentDialogOpen(false)
      setCompletingSession(null)
    } catch (error) {
      console.error('Error completing session payment:', error)
      showSnackbar('Failed to process payment. Please try again.', 'error')
    } finally {
      setProcessingPayment(false)
    }
  }

  const handleClosePaymentDialog = () => {
    if (!processingPayment) {
      setPaymentDialogOpen(false)
      setCompletingSession(null)
    }
  }

  const handleCloseClaimPreviewDialog = () => {
    if (!processingPayment) {
      setClaimPreviewDialogOpen(false)
      setCompletingSession(null)
    }
  }

  const handleConfirmClaimSubmission = async (sessionId: string) => {
    setProcessingPayment(true)
    try {
      const updatedSession = await completeSessionPayment(sessionId)
      onUpdateSession(updatedSession)
      setClaimPreviewDialogOpen(false)
      setCompletingSession(null)
    } catch (error) {
      console.error('Error submitting claim:', error)
      showSnackbar('Failed to submit claim. Please try again.', 'error')
    } finally {
      setProcessingPayment(false)
    }
  }

  const handleViewClaim = (sessionId: string) => {
    const session = contract.sessions.find(s => s.id === sessionId)
    if (session) {
      setCompletingSession(session)
      setIsClaimSubmitMode(false) // This is viewing an existing claim
      setClaimPreviewDialogOpen(true)
    }
  }

  const getStatusColor = (status: Contract['status']) => {
    switch (status) {
      case 'active':
        return 'info'
      case 'pending':
        return 'warning'
      case 'completed':
        return 'success'
      case 'cancelled':
        return 'error'
      default:
        return 'default'
    }
  }

  const handleESignClick = async () => {
    setSigningModalOpen(true)
    setSigningLoading(true)
    setSigningUrl(null)
    setSubmitting(false)
    // Reset refs when opening modal
    iframeLoadCountRef.current = 0
    hasSubmittedRef.current = false

    try {
      const response = await api.post('/docusign/get-signurl', {
        contractId: contract.id,
        type: 'therapist',
      })

      const url = response?.data?.signingUrl
      if (!url) {
        throw new Error('Signing URL missing')
      }

      setSigningUrl(url)
    } catch (error) {
      console.error('Error fetching contract sign URL:', error)
      showSnackbar(
        'Unable to load DocuSign envelope. Please try again.',
        'error'
      )
      setSigningModalOpen(false)
    } finally {
      setSigningLoading(false)
    }
  }

  // Auto-submit when signing is detected
  const handleAutoSubmit = useCallback(async () => {
    // Prevent duplicate submissions
    if (hasSubmittedRef.current || submitting) return
    hasSubmittedRef.current = true

    console.log('✅ Signing complete - auto-submitting...')
    setSubmitting(true)

    try {
      await api.get(`/contracts/therapist-sign/${contract.id}`)
      showSnackbar('Contract signed successfully.', 'success')

      if (onSignContract) {
        onSignContract(contract.id)
      }

      setTimeout(() => {
        setSigningModalOpen(false)
        setSigningUrl(null)
        window.location.reload()
      }, 1500)
    } catch (error) {
      console.error('Failed to confirm contract signature:', error)
      showSnackbar(
        'Unable to confirm signature. Please try again or contact support.',
        'error'
      )
      setSubmitting(false)
      hasSubmittedRef.current = false
    }
  }, [contract.id, onSignContract, submitting])

  // Handle iframe load events - detect when DocuSign navigates to success page
  const handleIframeLoad = useCallback(() => {
    iframeLoadCountRef.current += 1
    console.log(`📄 Iframe loaded (count: ${iframeLoadCountRef.current})`)

    // First load is the initial signing page
    // Second load (or more) means user navigated - likely to completion page
    if (iframeLoadCountRef.current >= 2) {
      handleAutoSubmit()
    }
  }, [handleAutoSubmit])

  // Listen for DocuSign postMessage events (backup detection)
  useEffect(() => {
    if (!signingModalOpen) return

    const handleMessage = (event: MessageEvent) => {
      const data = event.data

      // Check for string messages
      if (typeof data === 'string') {
        if (
          data.includes('signing_complete') ||
          data.includes('signingComplete') ||
          data.includes('Signing Complete')
        ) {
          handleAutoSubmit()
          return
        }
      }

      // Check for object messages
      if (typeof data === 'object' && data !== null) {
        const eventType = data.event || data.type || data.action || ''
        const status = data.status || ''

        if (
          eventType === 'signing_complete' ||
          eventType === 'signingComplete' ||
          status === 'signing_complete' ||
          data.signingComplete === true
        ) {
          handleAutoSubmit()
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [signingModalOpen, handleAutoSubmit])

  const handleCloseSigningModal = () => {
    if (signingLoading || submitting) {
      return
    }
    setSigningModalOpen(false)
    setSigningUrl(null)
    // Reset refs when closing
    iframeLoadCountRef.current = 0
    hasSubmittedRef.current = false
  }

  const handleCancelContract = () => {
    setCancelModalOpen(true)
    setCancelReason('')
  }

  const handleCloseCancelModal = () => {
    if (cancelLoading) {
      return
    }
    setCancelModalOpen(false)
    setCancelReason('')
  }

  const handleSubmitCancellation = async () => {
    if (!cancelReason.trim()) {
      showSnackbar(
        'Please provide a reason for rejecting the contract.',
        'warning'
      )
      return
    }

    setCancelLoading(true)
    try {
      await api.patch(`/contracts/status/${contract.id}`, {
        status: 'rejected',
        reason: cancelReason.trim(),
      })
      showSnackbar('Contract has been rejected.', 'success')
      setCancelModalOpen(false)
      setCancelReason('')
      navigate('/therapy-contracts')
    } catch (error) {
      console.error('Failed to reject contract:', error)
      showSnackbar('Unable to reject the contract. Please try again.', 'error')
    } finally {
      setCancelLoading(false)
    }
  }

  const handleDownloadContract = async () => {
    if (downloadLoading) {
      return
    }

    if (!contract.envelopeId) {
      console.warn('No envelope ID available for download')
      return
    }

    setDownloadLoading(true)

    try {
      const response = await api.get(
        `/docusign/download/${contract.envelopeId}`
      )
      const signedUrl = response?.data?.signedUrl

      if (!signedUrl) {
        throw new Error('Signed URL missing from response')
      }

      // Open the PDF in a new tab
      window.open(signedUrl, '_blank')
    } catch (error) {
      console.error('Error downloading contract document:', error)
      showSnackbar(
        'Failed to download contract document. Please try again.',
        'error'
      )
    } finally {
      setDownloadLoading(false)
    }
  }

  // Calendar utilities for Add New Session
  const today = useMemo(() => new Date(), [])
  const monthStart = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + monthOffset, 1),
    [today, monthOffset]
  )
  const daysInMonth = useMemo(
    () =>
      new Date(
        monthStart.getFullYear(),
        monthStart.getMonth() + 1,
        0
      ).getDate(),
    [monthStart]
  )
  const monthDates = useMemo(() => {
    const arr: (Date | null)[] = []
    const firstDayOfWeek = monthStart.getDay()
    for (let i = 0; i < firstDayOfWeek; i++) {
      arr.push(null)
    }
    for (let i = 1; i <= daysInMonth; i++) {
      arr.push(new Date(monthStart.getFullYear(), monthStart.getMonth(), i))
    }
    return arr
  }, [monthStart, daysInMonth])

  // Calendar utilities for Reschedule Session
  const rescheduleMonthStart = useMemo(
    () =>
      new Date(
        today.getFullYear(),
        today.getMonth() + rescheduleMonthOffset,
        1
      ),
    [today, rescheduleMonthOffset]
  )
  const rescheduleDaysInMonth = useMemo(
    () =>
      new Date(
        rescheduleMonthStart.getFullYear(),
        rescheduleMonthStart.getMonth() + 1,
        0
      ).getDate(),
    [rescheduleMonthStart]
  )
  const rescheduleMonthDates = useMemo(() => {
    const arr: (Date | null)[] = []
    const firstDayOfWeek = rescheduleMonthStart.getDay()
    for (let i = 0; i < firstDayOfWeek; i++) {
      arr.push(null)
    }
    for (let i = 1; i <= rescheduleDaysInMonth; i++) {
      arr.push(
        new Date(
          rescheduleMonthStart.getFullYear(),
          rescheduleMonthStart.getMonth(),
          i
        )
      )
    }
    return arr
  }, [rescheduleMonthStart, rescheduleDaysInMonth])

  const pad = (n: number) => String(n).padStart(2, '0')
  const formatDateKey = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  const formatDateForAPI = (d: Date) => {
    const year = d.getFullYear()
    const month = pad(d.getMonth() + 1)
    const day = pad(d.getDate())
    return `${year}-${month}-${day}`
  }
  const formatDateDisplayLong = (d: Date) =>
    d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })

  const isPastDate = (d: Date) => {
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    )
    const compare = new Date(d.getFullYear(), d.getMonth(), d.getDate())
    return compare < todayOnly
  }

  // Fetch available slots for selected date (Add Session)
  useEffect(() => {
    if (!selectedDate || !addSessionDialogOpen) {
      setSlotsForDate([])
      return
    }

    const therapistId = contract.therapist.id
    const contractId = contract.id

    // Note: serviceId might not be available in Contract type, using a placeholder
    const serviceId = contract.serviceId || ''

    if (!therapistId) return

    const formattedDate = formatDateForAPI(selectedDate)

      ; (async () => {
        setLoadingSlots(true)
        try {
          const res = await api.get(
            `/therapist/schedule/available-slots?therapistId=${encodeURIComponent(therapistId)}&date=${formattedDate}&serviceId=${serviceId || ''}&contractId=${encodeURIComponent(contractId)}`
          )
          const serverData = res?.data?.data ?? []
          const normalized: any[] = Array.isArray(serverData)
            ? serverData
              .map((s: any) => {
                if (!s) return null
                const start = s.startTime ?? s.start_time ?? s.start
                const end = s.endTime ?? s.end_time ?? s.end
                if (!start || !end) return null
                return {
                  _id: s._id,
                  startTime: start,
                  endTime: end,
                }
              })
              .filter(Boolean)
            : []
          setSlotsForDate(normalized)
        } catch (err) {
          console.error('Slot fetch error:', err)
          setSlotsForDate([])
        } finally {
          setLoadingSlots(false)
        }
      })()
  }, [selectedDate, contract.therapist.id, addSessionDialogOpen])

  // Fetch available slots for reschedule date
  useEffect(() => {
    if (!rescheduleDate || !rescheduleDialogOpen) {
      setRescheduleSlots([])
      return
    }

    const therapistId = contract.therapist.id
    const serviceId = contract.serviceId || ''

    if (!therapistId) return

    const formattedDate = formatDateForAPI(rescheduleDate)

      ; (async () => {
        setLoadingRescheduleSlots(true)
        try {
          const res = await api.get(
            `/therapist/schedule/available-slots?therapistId=${encodeURIComponent(therapistId)}&date=${formattedDate}&serviceId=${serviceId || ''}`
          )
          const serverData = res?.data?.data ?? []
          const normalized: any[] = Array.isArray(serverData)
            ? serverData
              .map((s: any) => {
                if (!s) return null
                const start = s.startTime ?? s.start_time ?? s.start
                const end = s.endTime ?? s.end_time ?? s.end
                if (!start || !end) return null
                return {
                  _id: s._id,
                  startTime: start,
                  endTime: end,
                }
              })
              .filter(Boolean)
            : []
          setRescheduleSlots(normalized)
        } catch (err) {
          console.error('Reschedule slot fetch error:', err)
          setRescheduleSlots([])
        } finally {
          setLoadingRescheduleSlots(false)
        }
      })()
  }, [rescheduleDate, contract.therapist.id, rescheduleDialogOpen])

  // Handlers for Add New Session
  const handleAddNewSession = () => {
    if (isCancelledContract) return
    setSelectedDate(today)
    setSelectedSlots({})
    setPaymentMethod('out_of_pocket')
    setAddSessionDialogOpen(true)
  }

  const handleSelectSlot = (date: Date, slot: any) => {
    const dateKey = formatDateKey(date)
    const start = slot.startTime
    const end = slot.endTime

    // Toggle slot selection
    if (
      selectedSlots[dateKey] &&
      selectedSlots[dateKey].startTime === start &&
      selectedSlots[dateKey].endTime === end
    ) {
      const copy = { ...selectedSlots }
      delete copy[dateKey]
      setSelectedSlots(copy)
      return
    }

    // Check max 5 sessions limit
    const selectedCount = Object.keys(selectedSlots).length
    if (!selectedSlots[dateKey] && selectedCount >= 10) {
      showSnackbar(
        'You can add up to 10 sessions at a time. Remove one to add another.',
        'warning'
      )
      return
    }

    setSelectedSlots(prev => ({
      ...prev,
      [dateKey]: { slotId: slot._id, startTime: start, endTime: end },
    }))
  }

  const removeSelectedSlot = (dateKey: string) => {
    const copy = { ...selectedSlots }
    delete copy[dateKey]
    setSelectedSlots(copy)
  }

  const handleAddSessionSubmit = async () => {
    const selectedCount = Object.keys(selectedSlots).length
    if (selectedCount === 0) {
      showSnackbar('Please select at least one session slot', 'error')
      return
    }
    if (!paymentMethod) {
      showSnackbar('Please select a payment method', 'error')
      return
    }

    try {
      setIsSubmittingSession(true)

      // Convert selectedSlots to bookings array format
      const bookings = Object.entries(selectedSlots).map(([dateKey, slot]) => {
        const parts = dateKey.split('-').map(Number)
        const dateObj = new Date(parts[0], parts[1] - 1, parts[2])
        const formattedDate = formatDateForAPI(dateObj)
        const sessionSlot = `${slot.startTime}-${slot.endTime}`

        return {
          date: formattedDate,
          slot: sessionSlot,
        }
      })

      const payload = {
        contract_id: contract.id,
        bookings: bookings,
        payment_method: paymentMethod,
      }

      const res = await api.post('/contracts/session-create', payload)
      const successMessage =
        res?.data?.message ||
        `${selectedCount} session${selectedCount > 1 ? 's' : ''} added successfully!`
      showSnackbar(successMessage, 'success')
      setAddSessionDialogOpen(false)
      setSelectedSlots({})
      setSelectedDate(today)
      setPaymentMethod('')
      setAreYouSureDialogOpen(false)

      // Refresh contract data to show the newly added sessions
      if (onRefreshContract) {
        await onRefreshContract()
      }
    } catch (error: any) {
      console.error('Error adding sessions:', error)
      const errorMessage =
        error.response?.data?.message || 'Failed to add sessions. Please try again.'
      showSnackbar(errorMessage, 'error')
    } finally {
      setIsSubmittingSession(false)
    }
  }

  // Handler for Add Note (without specific session)
  const handleAddNote = () => {
    if (isCancelledContract) return
    // Use the first session if available, otherwise create a placeholder session
    if (contract.sessions.length > 0) {
      setSelectedSession(contract.sessions[0])
    } else {
      // Create a minimal session for the note editor
      const dummySession: Session = {
        id: 'temp',
        contractId: contract.id,
        scheduledAt: new Date().toISOString(),
        status: 'scheduled',
        locationType: 'telehealth',
        durationMinutes: 45,
        evv: {
          checkInAt: undefined,
          checkOutAt: undefined,
          lat: 0,
          lng: 0,
        },
        notes: [],
        claimable: false,
        paymentInfo: {
          paymentType: 'insurance',
          totalAmount: 0,
          patientOwedAmount: 0,
          paymentStatus: 'pending',
          claimStatus: 'pending',
        },
      }
      setSelectedSession(dummySession)
    }
    setEditingNote(undefined)
    setNoteEditorOpen(true)
  }

  // Handlers for Agency Messaging
  const handleMessageMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMessageMenuAnchor(event.currentTarget)
  }

  const handleMessageMenuClose = () => {
    setMessageMenuAnchor(null)
  }

  const { user } = useAppSelector(state => state.auth)
  const agencyId = (() => {
    const u = user as any
    return u?.agencyId || (typeof u?.agency === 'string' ? u?.agency : u?.agency?._id) || u?.id || ''
  })()

  const handleOpenAgencyChat = async (type: 'patient' | 'provider') => {
    handleMessageMenuClose()
    setMessageTarget(type)
    setAgencyConversation(null)
    setAgencyConversationError(null)
    setAgencyConversationLoading(true)
    setChatDialogOpen(true)

    try {
      let conversation: ChatConversation
      if (type === 'patient') {
        const patientId = contract.patient.id
        conversation = await createConversationPatientAgency({
          patientId,
          agencyId,
        })
      } else {
        const therapistId = contract.therapist.id
        conversation = await createConversationTherapistAgency({
          therapistId,
          agencyId,
        })
      }

      const chatConversation: ChatConversation = {
        conversationSid: resolveTwilioConversationSid(
          conversation.conversationSid,
          conversation.conversationId
        ),
        conversationId: conversation.conversationId,
        therapistId: contract.therapist.id,
        patientId: contract.patient.id,
        reused: conversation.reused,
        agencyId: conversation.agencyId,
      }
      setAgencyConversation(chatConversation)
    } catch (error: any) {
      console.error('Failed to create agency conversation:', error)
      setAgencyConversationError(
        error?.response?.data?.message ||
        (error instanceof Error ? error.message : 'Failed to initialize chat. Please try again.')
      )
    } finally {
      setAgencyConversationLoading(false)
    }
  }

  // Fetch PoC documents list
  const fetchPoCDocuments = async () => {
    setPocDocumentsLoading(true)
    try {
      const apiDocs = await pocApi.getPoCList(contract.id)
      // Convert API format to component format
      const convertedDocs: PoCDocument[] = apiDocs.map(doc => ({
        id: doc._id,
        documentName: `PoC_${doc._id.slice(-6)}.pdf`,
        poc_name: (doc as any).poc_name,
        status: doc.poc_status,
        createdOn: doc.createdAt,
        certified_license_url: doc?.certified_license,
        certified_by: doc.certified_by
          ? { name: doc.certified_by.name }
          : undefined,
        authorizationStatus:
          doc.authorization_status === 'pending'
            ? 'preauthorization-pending'
            : doc.authorization_status === 'submitted'
              ? 'submitted-for-preauthorization'
              : doc.authorization_status === 'approved'
                ? 'preauthorization-completed'
                : doc.authorization_status === 'denied'
                  ? 'preauthorization-declined'
                  : 'preauthorization-pending',
        authorizationNumber: doc.pre_auth_id,
        preauthorizationDocsUrl: undefined,
        apiDocument: doc, // Store full API document for modal display
        pdf_url: (doc as ApiPoCDocument).pdf_url,
        is_complete: (doc as any).is_complete,
        auth_submit_date: (doc as any).auth_submit_date,
        manager_signature: (doc as any).manager_signature,

      }))
      setPocDocuments(convertedDocs)
    } catch (error: any) {
      console.error('Error fetching PoC documents:', error)
      showSnackbar(
        error.response?.data?.message || 'Failed to fetch PoC documents',
        'error'
      )
    } finally {
      setPocDocumentsLoading(false)
    }
  }

  // Handler for Generate PoC
  const handleGeneratePoC = (sessionId: string) => {
    const session = contract.sessions.find(s => s.id === sessionId)
    if (session) {
      setSelectedSessionForPoC(session)
      setPocDialogOpen(true)
    }
  }

  // Handler for PoC generation success
  const handlePoCSuccess = async (message?: string) => {
    const response = await fetchPoCDocuments()
    showSnackbar(message || 'POC saved successfully', 'success')

    // Update the session's isPocGenerated flag to reflect changes immediately
    if (selectedSessionForPoC) {
      const updatedSession: Session = {
        ...selectedSessionForPoC,
        isPocGenerated: true,
      }

      // Notify parent to update the session
      if (onUpdateSession) {
        onUpdateSession(updatedSession)
      }
    }
  }

  // Handler for Submit for Preauthorization
  const handleSubmitForPreauth = (documentId: string) => {
    const doc = pocDocuments.find(d => d.id === documentId)
    if (doc && (doc as any).apiDocument) {
      setSelectedPoCForPreauth((doc as any).apiDocument)
      setPreauthSubmissionDialogOpen(true)
    }
  }

  const handlePreauthSubmission = async (submitDate: string) => {
    if (!selectedPoCForPreauth) return

    setPreauthLoading(true)
    try {
      await pocApi.updatePoCStatus({
        poc_id: selectedPoCForPreauth._id,
        pre_auth_status: 'submitted',
        pre_auth_submit_date: submitDate,
      })
      showSnackbar('PoC submitted for preauthorization successfully', 'success')
      setPreauthSubmissionDialogOpen(false)
      setSelectedPoCForPreauth(null)
      await fetchPoCDocuments()
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message ||
        'Failed to submit for preauthorization',
        'error'
      )
    } finally {
      setPreauthLoading(false)
    }
  }

  // Handler for Preauthorization Completed
  const handlePreauthCompleted = (documentId: string) => {
    const doc = pocDocuments.find(d => d.id === documentId)
    if (doc && (doc as any).apiDocument) {
      setSelectedPoCForPreauth((doc as any).apiDocument)
      setPreauthCompletedDialogOpen(true)
    }
  }

  const handlePreauthCompletedSubmit = async (data: {
    pre_auth_id: string
    approval_date: string
    authorized_no_session: number
    auth_service_period: string
  }) => {
    if (!selectedPoCForPreauth) return

    setPreauthLoading(true)
    try {
      await pocApi.updatePoCStatus({
        poc_id: selectedPoCForPreauth._id,
        pre_auth_status: 'approved',
        pre_auth_date: data.approval_date,
        pre_auth_id: data.pre_auth_id,
        authorized_no_session: data.authorized_no_session,
        auth_service_period: data.auth_service_period, // This is now an ISO date string
      })
      showSnackbar('Preauthorization completed successfully', 'success')
      setPreauthCompletedDialogOpen(false)
      setSelectedPoCForPreauth(null)
      await fetchPoCDocuments()
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || 'Failed to complete preauthorization',
        'error'
      )
    } finally {
      setPreauthLoading(false)
    }
  }

  // Handler for Preauthorization Declined
  const handlePreauthDeclined = (documentId: string) => {
    const doc = pocDocuments.find(d => d.id === documentId)
    if (doc && (doc as any).apiDocument) {
      setSelectedPoCForPreauth((doc as any).apiDocument)
      setPreauthDeclinedDialogOpen(true)
    }
  }

  const handlePreauthDeclinedSubmit = async (data: {
    denial_date: string
    denial_reason: string
  }) => {
    if (!selectedPoCForPreauth) return

    setPreauthLoading(true)
    try {
      await pocApi.updatePoCStatus({
        poc_id: selectedPoCForPreauth._id,
        pre_auth_status: 'denied',
        denial_date: data.denial_date,
        denial_reason: data.denial_reason,
      })
      showSnackbar('Preauthorization declined successfully', 'success')
      setPreauthDeclinedDialogOpen(false)
      setSelectedPoCForPreauth(null)
      await fetchPoCDocuments()
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || 'Failed to decline preauthorization',
        'error'
      )
    } finally {
      setPreauthLoading(false)
    }
  }

  // Handler for Reschedule Session
  const handleReschedule = (session: Session) => {
    if (isCancelledContract) return
    setSelectedSessionForReschedule(session)
    setRescheduleDate(today)
    setSelectedRescheduleSlot(null)
    setRescheduleDialogOpen(true)
  }

  const handleRescheduleSubmit = async () => {
    if (
      !rescheduleDate ||
      !selectedRescheduleSlot ||
      !selectedSessionForReschedule
    ) {
      showSnackbar('Please select a date and time slot', 'error')
      return
    }

    try {
      setIsSubmittingReschedule(true)
      const formattedDate = formatDateForAPI(rescheduleDate)
      const newSlot = `${selectedRescheduleSlot.startTime}-${selectedRescheduleSlot.endTime}`

      const payload = {
        session_id:
          selectedSessionForReschedule._id || selectedSessionForReschedule.id,
        new_date: formattedDate,
        new_slot: newSlot,
      }

      await api.post('/contracts/session-reschedule', payload)
      showSnackbar('Session rescheduled successfully!', 'success')
      setRescheduleDialogOpen(false)
      setSelectedRescheduleSlot(null)
      setRescheduleDate(today)
      setSelectedSessionForReschedule(null)

      // Refresh contract data
      if (onRefreshContract) {
        await onRefreshContract()
      }
    } catch (error: any) {
      console.error('Error rescheduling session:', error)
      showSnackbar(
        error.response?.data?.message ||
        'Failed to reschedule session. Please try again.',
        'error'
      )
    } finally {
      setIsSubmittingReschedule(false)
    }
  }

  // Handler for Cancel Session
  const handleCancel = (session: Session) => {
    setSelectedSessionForCancel(session)
    setCancelSessionReason('')
    setCancelSessionDialogOpen(true)
  }

  const handleCancelSubmit = async () => {
    if (!cancelSessionReason.trim() || !selectedSessionForCancel) {
      showSnackbar('Please provide a reason for cancellation', 'error')
      return
    }

    try {
      setIsSubmittingCancel(true)
      const payload = {
        session_id: selectedSessionForCancel._id || selectedSessionForCancel.id,
        reason: cancelSessionReason,
      }

      await api.post('/contracts/session-cancel', payload)
      showSnackbar('Session cancelled successfully!', 'success')
      setCancelSessionDialogOpen(false)
      setCancelSessionReason('')
      setSelectedSessionForCancel(null)

      // Refresh contract data
      if (onRefreshContract) {
        await onRefreshContract()
      }
    } catch (error: any) {
      console.error('Error cancelling session:', error)
      showSnackbar(
        error.response?.data?.message ||
        'Failed to cancel session. Please try again.',
        'error'
      )
    } finally {
      setIsSubmittingCancel(false)
    }
  }

  // Handle Complete Contract
  const handleOpenCompleteContractDialog = () => {
    setCompleteContractDialogOpen(true)
  }

  const handleCloseCompleteContractDialog = () => {
    if (!completingContract) {
      setCompleteContractDialogOpen(false)
    }
  }

  const handleCompleteContract = async () => {
    if (isCancelledContract || !isActiveContract) return

    try {
      setCompletingContract(true)

      // API endpoint: PATCH /api/contracts/{contractId}/complete
      await api.patch(`/contracts/${contract.id}/complete`)

      showSnackbar('Contract marked as completed successfully', 'success')
      setCompleteContractDialogOpen(false)

      // Refresh contract data
      if (onRefreshContract) {
        await onRefreshContract()
      }
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || 'Failed to complete contract',
        'error'
      )
    } finally {
      setCompletingContract(false)
    }
  }

  // Fetch PoC documents on mount and when dialog opens
  useEffect(() => {
    fetchPoCDocuments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract.id])

  // Refresh PoC documents when dialog opens
  useEffect(() => {
    if (pocDocumentsDialogOpen) {
      fetchPoCDocuments()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pocDocumentsDialogOpen])

  const handleSendContract =async (pepoleType:string) => {
    try {
      const response = await api.get(`/contracts/send-contract-mail/${contract.id}/${pepoleType}`)
      if (response.status==200) {
        showSnackbar(response.data.message, 'success')
      }else{
        showSnackbar(response.data.message, 'error')
      }
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || 'Failed to send contract',
        'error'
      )
    }
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        Back to Contracts
      </Button>

      {/* E-Signature Alert for Pending Contracts */}
      {/* {needsTherapistSignature && (
        <Fade in timeout={400}>
          <Alert
            severity="warning"
            icon={<InfoIcon />}
            sx={{ mb: 3, alignItems: 'center' }}
            action={
              <Box display="flex" gap={1} alignItems="center">
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<DrawIcon />}
                  onClick={handleESignClick}
                  variant="outlined"
                  sx={{
                    textWrap: 'nowrap',
                    borderColor: 'currentColor',
                    '&:hover': {
                      borderColor: 'currentColor',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  disabled={signingLoading}
                >
                  {signingLoading ? 'Loading...' : 'E-Sign Contract'}
                </Button>
                <Button
                  color="error"
                  size="small"
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  onClick={handleCancelContract}
                  disabled={cancelLoading}
                  sx={{
                    textWrap: 'nowrap',
                    borderColor: 'error.main',
                    '&:hover': {
                      borderColor: 'error.main',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Reject Contract
                </Button>
              </Box>
            }
          >
            <AlertTitle sx={{ fontWeight: 600 }}>
              Contract Awaiting Your Signature
            </AlertTitle>
            Review and sign the contract to get started. After signing,
            you&apos;ll be able to add sessions and start your treatment
            sessions.
          </Alert>
        </Fade>
      )} */}

      {/* Cancelled Contract Banner */}
      {isCancelledContract && (
        <Fade in timeout={300}>
          <Alert
            severity="error"
            icon={<CancelIcon />}
            sx={{
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-icon': {
                fontSize: 28,
              },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600 }}>
              Contract Terminated
            </AlertTitle>
            This contract has been cancelled and is no longer active. All
            actions including adding sessions, rescheduling sessions, cancelling
            sessions, completing sessions, clocking in/out, generating POC, and
            adding notes have been disabled.
          </Alert>
        </Fade>
      )}

      <Grow in timeout={300}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="start"
              mb={2}
            >
              <Box>
                <Typography
                  variant="h4"
                  gutterBottom
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  {contract.contract_title || contract.title}
                  <Chip
                    label={contract.status}
                    color={getStatusColor(contract.status)}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Contract ID: {contract.externalId || contract.id}
                  </Typography>
                  {contract.envelopeId && (
                    <Tooltip placement="top" title="Download Contract" arrow>
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        <IconButton
                          size="small"
                          onClick={handleDownloadContract}
                          disabled={downloadLoading}
                          sx={{
                            padding: 0.5,
                            position: 'relative',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                          aria-label="Download contract"
                        >
                          {downloadLoading ? (
                            <CircularProgress
                              size={18}
                              thickness={5}
                              color="inherit"
                            />
                          ) : (
                            <DownloadIcon fontSize="small" color="action" />
                          )}
                        </IconButton>
                      </Box>
                    </Tooltip>
                  )}
                  {!hasNoSessions && (
                    <>
                      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Total Amount: ${(contract.totalAmount ?? 0).toFixed(2)}
                      </Typography>
                    </>
                  )}
                  {contract.status === 'pending' && (
                    <>
                      {!(contract.patientSigned ) ? (
                        <>
                          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                          <Tooltip title="Send contract to email for signature to patient" arrow placement="top">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSendContract('patient')}
                              startIcon={<SendIcon sx={{ fontSize: '14px !important' }} />}
                              sx={{
                                background: 'linear-gradient(135deg, #1E1743 0%, #2d235f 100%)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '20px',
                                px: 1.5,
                                py: 0.25,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: '0 4px 10px rgba(30, 23, 67, 0.2)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #2d235f 0%, #1E1743 100%)',
                                  boxShadow: '0 6px 14px rgba(30, 23, 67, 0.3)',
                                  transform: 'translateY(-1px)',
                                },
                                '&:active': {
                                  transform: 'translateY(0)',
                                }
                              }}
                            >
                              Send Contract to Patient
                            </Button>
                          </Tooltip>
                        </>
                      ) : !(contract.therapistSigned) ? (
                        <>
                          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                          <Tooltip title="Send contract to email for signature to provider" arrow placement="top">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSendContract('therapist')}
                              startIcon={<SendIcon sx={{ fontSize: '14px !important' }} />}
                              sx={{
                                background: 'linear-gradient(135deg, #1E1743 0%, #2d235f 100%)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '20px',
                                px: 1.5,
                                py: 0.25,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: '0 4px 10px rgba(30, 23, 67, 0.2)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #2d235f 0%, #1E1743 100%)',
                                  boxShadow: '0 6px 14px rgba(30, 23, 67, 0.3)',
                                  transform: 'translateY(-1px)',
                                },
                                '&:active': {
                                  transform: 'translateY(0)',
                                }
                              }}
                            >
                              Send to Provider
                            </Button>
                          </Tooltip>
                        </>
                      ) : null}
                    </>
                  )}
             
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Per Session: ${Number(contractAmount).toFixed(2)}
                  </Typography>
                  {sessionLength && (
                    <>
                      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Session Length: {sessionLength} minutes
                      </Typography>
                    </>
                  )}

                </Box>
              </Box>
              <Box display="flex" gap={1.5} flexWrap="wrap">
                {!isCompletedContract && (
                  <Tooltip placement="top" title={
                    isCancelledContract
                      ? 'Contract is cancelled. Actions are disabled.'
                      : isPendingContract
                        ? 'Sign the contract first to add sessions'
                        : ''
                  }
                    arrow
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddNewSession}
                        disabled={isCancelledContract || isPendingContract}
                        sx={{
                          background: 'linear-gradient(135deg, #7A6FA1 0%, #8E85C9 100%)',
                          boxShadow: '0 4px 12px rgba(46, 125, 175, 0.25)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #7A6FA1 0%, #8E85C9 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(46, 125, 175, 0.35)',
                          },
                          '&:disabled': {
                            background: 'grey.300',
                            color: 'grey.500',
                            boxShadow: 'none',
                          },
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 2,
                          px: 2.5,
                          py: 1,
                          transition: 'all 0.2s ease',
                        }}
                      >
                        Add Session
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Button
                  variant="outlined"
                  startIcon={<MessageIcon />}
                  onClick={handleMessageMenuOpen}
                  sx={{
                    borderColor: 'grey.300',
                    color: 'text.primary',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    '&:hover': {
                      borderColor: '#7A6FA1',
                      backgroundColor: 'rgba(46, 125, 175, 0.04)',
                      color: '#7A6FA1',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: 2,
                    px: 2.5,
                    py: 1,
                    transition: 'all 0.2s ease',
                  }}
                >
                  Message
                </Button>
                <Menu
                  anchorEl={messageMenuAnchor}
                  open={Boolean(messageMenuAnchor)}
                  onClose={handleMessageMenuClose}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      borderRadius: 2,
                      minWidth: 180,
                      mt: 0.5,
                      '& .MuiMenuItem-root': {
                        py: 1.5,
                        px: 2.5,
                        fontSize: '0.95rem',
                        fontWeight: 500,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                >
                  <MenuItem onClick={() => handleOpenAgencyChat('patient')}>
                    Message patient
                  </MenuItem>
                  <MenuItem onClick={() => handleOpenAgencyChat('provider')}>
                    Message provider
                  </MenuItem>
                </Menu>
                {(isActiveContract && contract.completedSessions > 0) && (
                  <Tooltip placement="top" title="Mark this contract as completed" arrow>
                    <IconButton
                      onClick={handleOpenCompleteContractDialog}
                      sx={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        borderRadius: 2,
                        width: 44,
                        height: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 16px rgba(16, 185, 129, 0.35)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      <CheckCircleIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PersonIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Patient
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {contract.patient.name}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PersonIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Provider
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {contract.therapist?.name || 'Unknown provider'}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Start Date
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {format(new Date(contract.startDate), 'MMMM dd, yyyy')}
                    </Typography>
                  </Box>
                </Box>

                {/* Upcoming Session */}
                {(() => {
                  const nextAvailableSession = contract.sessions
                    .filter(s => s.status === 'scheduled' && !s.evv.checkInAt)
                    .sort(
                      (a, b) =>
                        new Date(a.scheduledAt).getTime() -
                        new Date(b.scheduledAt).getTime()
                    )[0]

                  return nextAvailableSession ? (
                    <Box display="flex" alignItems="center" gap={1} mt={2}>
                      <ScheduleIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Next Session
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {format(new Date(nextSessionDateprop), 'MMMM dd, yyyy')}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {nextAvailableSession.locationType} •{' '}
                          {nextAvailableSession.durationMinutes || 45} min
                        </Typography>
                      </Box>
                    </Box>
                  ) : null
                })()}
              </Grid>

              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    gutterBottom
                  >
                    Treatment Progress
                  </Typography>
                  {hasNoSessions ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px dashed',
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Typography variant="h5" fontWeight={600}>
                          0 sessions
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        No sessions have been scheduled yet. Use the &quot;Add
                        New Session&quot; button above to schedule treatment
                        sessions.
                      </Typography>
                    </Paper>
                  ) : (
                    <>
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Typography variant="h5" fontWeight={600}>
                          {contract.completedSessions} /{' '}
                          {contract.plannedSessions}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          sessions
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          mb: 1,
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 5,
                            background:
                              'linear-gradient(45deg, #10b981 30%, #34d399 90%)',
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        mb={2}
                      >
                        {progress.toFixed(0)}% complete
                      </Typography>
                    </>
                  )}

                  {/* PoC Document CTA Card */}
                  <Paper
                    elevation={0}
                    onClick={() => setPocDocumentsDialogOpen(true)}
                    sx={{
                      p: 2,
                      mt: 2,
                      border: '2px solid',
                      borderColor: '#7A6FA1',
                      borderRadius: 2,
                      background:
                        'linear-gradient(135deg, rgba(46, 125, 175, 0.05) 0%, rgba(46, 125, 175, 0.02) 100%)',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(46, 125, 175, 0.2)',
                        borderColor: '#7A6FA1',
                        background:
                          'linear-gradient(135deg, rgba(46, 125, 175, 0.1) 0%, rgba(46, 125, 175, 0.05) 100%)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background:
                          'linear-gradient(90deg, #7A6FA1 0%, #8E85C9 100%)',
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background:
                            'linear-gradient(135deg, #7A6FA1 0%, #8E85C9 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 12px rgba(46, 125, 175, 0.3)',
                        }}
                      >
                        <DescriptionIcon
                          sx={{ color: 'white', fontSize: 28 }}
                        />
                      </Box>
                      <Box flex={1}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ color: '#7A6FA1', mb: 0.5 }}
                        >
                          PoC Document
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', lineHeight: 1.4 }}
                        >
                          View and manage Plan of Care documents
                        </Typography>
                      </Box>
                      {/* <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: '#2E7DAF',
                          fontWeight: 600,
                        }}
                      >
                        <Typography variant="caption" sx={{ mr: 0.5 }}>
                          {pocDocuments.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          doc{pocDocuments.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box> */}
                    </Box>
                  </Paper>
                </Box>
              </Grid>
            </Grid>

            {/* Treatment Goals */}
            {contract.treatmentGoals && contract.treatmentGoals.length > 0 && (
              <Box>
                <Button
                  startIcon={<AssessmentIcon />}
                  endIcon={showGoals ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setShowGoals(!showGoals)}
                  size="small"
                >
                  Treatment Goals
                </Button>
                <Collapse in={showGoals}>
                  <Paper
                    elevation={0}
                    sx={{ p: 2, mt: 1, backgroundColor: 'grey.50' }}
                  >
                    <List dense>
                      {contract.treatmentGoals.map((goal, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={`${index + 1}. ${goal}`} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Collapse>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grow>

      {/* Sessions List */}
      <Fade in timeout={500}>
        <Card>
          <CardContent>
            <Box mb={2}>
              <Typography variant="h5">Sessions</Typography>
            </Box>

            <Box>
              {contract.sessions.map((session, index) => {
                // Find the next available session (first scheduled/rescheduled session without check-in)
                const nextAvailableSession = contract.sessions
                  .filter(
                    s =>
                      (s.status === 'scheduled' ||
                        s.status === 'rescheduled') &&
                      !s.evv.checkInAt
                  )
                  .sort(
                    (a, b) =>
                      new Date(a.scheduledAt).getTime() -
                      new Date(b.scheduledAt).getTime()
                  )[0]

                const isNextAvailableSession =
                  nextAvailableSession?.id === session.id

                return (
                  <SessionRow
                    key={session.id}
                    session={session}
                    patient={contract.patient}
                    therapist={contract.therapist}
                    contractId={contract.id}
                    onStartEVV={mode => handleStartEVV(session, mode)}
                    onUpdateSession={onUpdateSession}
                    onOpenNote={note => handleOpenNote(session, note)}
                    onRaiseInvoice={handleRaiseInvoice}
                    onCompleteSession={handleCompleteSession}
                    onViewClaim={handleViewClaim}
                    onGeneratePoC={handleGeneratePoC}
                    onReschedule={() => handleReschedule(session)}
                    onCancel={() => handleCancel(session)}
                    onShowSnackbar={showSnackbar}
                    delay={index * 50}
                    isNextAvailableSession={isNextAvailableSession}
                    readOnly={isPendingContract || isCancelledContract}
                    isCancelledContract={isCancelledContract}
                    isCompletedContract={isCompletedContract}
                  />
                )
              })}
            </Box>

            {contract.sessions.length === 0 && (
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  px: 3,
                  borderRadius: 3,
                  border: '1px dashed',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.light',
                    bgcolor: 'grey.100',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    mb: 2,
                    opacity: 0.9,
                  }}
                >
                  <ScheduleIcon sx={{ fontSize: 32 }} />
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="text.primary"
                  gutterBottom
                >
                  No sessions scheduled yet
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ maxWidth: 360 }}
                >
                  Use the &quot;Add New Session&quot; button above to schedule
                  sessions for this contract.
                </Typography>
              </Paper>
            )}
          </CardContent>
        </Card>
      </Fade>

      {/* Contract Timeline, Documents, and Contract Documents Sections */}
      <Box sx={{ mt: 3 }}>
        <Grid container spacing={3}>
          {/* Contract Timeline - Top Left */}
          <Grid item xs={12} md={6}>
            <ContractTimeline timeline={timeline} loading={timelineLoading} />
          </Grid>

          {/* Documents Section - Top Right */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            {/* Contract Documents - Bottom Full Width */}
            <ContractDocuments
              documents={contractDocuments}
              loading={contractDocumentsLoading}
              onDownload={onDownloadContractDocument}
            />
            <DocumentsSection
              contractId={(contract as any)._id || contract.id}
              documents={documents}
              loading={documentsLoading}
              onUpload={onUploadDocument}
            />
          </Grid>
        </Grid>
      </Box>

      {/* EVV Modal - Only for check-out */}
      {selectedSession && evvMode === 'check-out' && (
        <EvvModal
          open={evvModalOpen}
          onClose={() => setEvvModalOpen(false)}
          session={selectedSession}
          mode={evvMode}
          onSuccess={handleEVVSuccess}
        />
      )}

      {/* Note Editor */}
      {selectedSession && (
        <NoteEditor
          open={noteEditorOpen}
          onClose={() => setNoteEditorOpen(false)}
          session={selectedSession}
          sessionData={sessionData}
          contract={contract}
          existingNote={editingNote}
          onSuccess={handleNoteSuccess}
          readOnly={isCancelledContract}
        />
      )}

      {/* Payment Completion Dialog - Self Pay */}
      {/* <PaymentCompletionDialog
        open={paymentDialogOpen}
        session={completingSession}
        onClose={handleClosePaymentDialog}
        onConfirm={handleConfirmPaymentCompletion}
        loading={processingPayment}
      /> */}

      {/* Claim Preview Dialog - Insurance */}
      {/* <ClaimPreviewDialog
        open={claimPreviewDialogOpen}
        session={completingSession}
        patient={contract.patient}
        therapistName={contract.therapist.name}
        therapistNPI={contract.therapist.npi || '1234567890'}
        onClose={handleCloseClaimPreviewDialog}
        onConfirm={handleConfirmClaimSubmission}
        loading={processingPayment}
        isSubmitMode={isClaimSubmitMode}
      /> */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            ...(snackbar.severity === 'error' && {
              backgroundColor: '#d32f2f',
              color: '#fff',
              '& .MuiAlert-icon': {
                color: '#fff',
              },
            }),
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={cancelModalOpen}
        onClose={handleCloseCancelModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject Contract</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Please provide a reason for rejecting this contract.
          </Typography>
          <TextField
            multiline
            minRows={4}
            fullWidth
            placeholder="Reason for rejecting..."
            value={cancelReason}
            onChange={event => setCancelReason(event.target.value)}
            disabled={cancelLoading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCancelModal} disabled={cancelLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleSubmitCancellation}
            disabled={cancelLoading}
          >
            {cancelLoading ? 'Rejecting...' : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={signingModalOpen}
        onClose={handleCloseSigningModal}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '90vw',
            height: '90vh',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
            flexShrink: 0,
          }}
        >
          Provider Signature
          <IconButton
            size="small"
            onClick={handleCloseSigningModal}
            disabled={signingLoading || submitting}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Loading State */}
          {signingLoading && (
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              gap={2}
              p={3}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Preparing DocuSign envelope...
              </Typography>
            </Box>
          )}

          {/* Submitting State - Auto-submitting after signing complete */}
          {!signingLoading && submitting && (
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              p={8}
              sx={{
                background:
                  'linear-gradient(to bottom, #f0fdf4 0%, #ffffff 100%)',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: 80,
                  height: 80,
                  mb: 3,
                  bgcolor: '#10b981',
                  borderRadius: '50%',
                  boxShadow: 3,
                }}
              >
                <FiCheckCircle style={{ fontSize: '2.5rem', color: 'white' }} />
              </Box>
              <Typography
                variant="h5"
                sx={{ mb: 2, fontWeight: 600, color: '#15803d' }}
              >
                Signing Complete!
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  textAlign: 'center',
                  maxWidth: 400,
                  color: '#6b7280',
                }}
              >
                Please wait while we confirm your signature...
              </Typography>
              <CircularProgress size={32} sx={{ color: '#10b981' }} />
            </Box>
          )}

          {/* DocuSign iframe - Show when signing */}
          {!signingLoading && !submitting && signingUrl && (
            <Box
              component="iframe"
              src={signingUrl}
              sx={{
                flex: 1,
                width: '100%',
                border: 0,
                minHeight: 0,
              }}
              title="DocuSign Envelope"
              onLoad={handleIframeLoad}
            />
          )}

          {/* Error State */}
          {!signingLoading && !submitting && !signingUrl && (
            <Box p={3}>
              <Typography variant="body2" color="text.secondary">
                Unable to load the DocuSign envelope. Please try again.
              </Typography>
            </Box>
          )}
        </DialogContent>

        {/* Footer - Only show when signing is in progress */}
        {!signingLoading && !submitting && (
          <Box
            sx={{
              px: 3,
              py: 2,
              flexShrink: 0,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: '#f9fafb',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Complete signing in the document above and click "Finish".
            </Typography>
          </Box>
        )}
      </Dialog>

      {/* Add New Session Dialog */}
      <Dialog
        open={addSessionDialogOpen}
        onClose={() => !isSubmittingSession && setAddSessionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Session</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 2, mb: 2 }}>
            {/* Calendar and Time Slots Side by Side */}
            <Grid container spacing={3}>
              {/* Calendar */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => setMonthOffset(m => m - 1)}
                      >
                        <FiChevronLeft />
                      </IconButton>
                      <Typography variant="body1" fontWeight={600}>
                        {monthStart.toLocaleString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        })}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setMonthOffset(m => m + 1)}
                      >
                        <FiChevronRight />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Select a day *
                    </Typography>
                  </Box>

                  <Grid container spacing={0.5} sx={{ mb: 2 }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                      d => (
                        <Grid item xs={12 / 7} key={d}>
                          <Typography
                            variant="caption"
                            textAlign="center"
                            display="block"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            {d}
                          </Typography>
                        </Grid>
                      )
                    )}
                  </Grid>

                  <Grid container spacing={0.5}>
                    {monthDates.map((d, index) => {
                      if (d === null) {
                        return (
                          <Grid item xs={12 / 7} key={`empty-${index}`}>
                            <Box sx={{ aspectRatio: '1' }} />
                          </Grid>
                        )
                      }

                      const key = formatDateKey(d)
                      const disabled = isPastDate(d)
                      const isActive =
                        selectedDate && formatDateKey(selectedDate) === key
                      const dateSelected = Boolean(selectedSlots[key])

                      return (
                        <Grid item xs={12 / 7} key={key}>

                          <Button
                            onClick={() => { !disabled && setSelectedDate(d) }}
                            disabled={disabled}
                            fullWidth
                            sx={{
                              aspectRatio: '1',
                              minWidth: 0,
                              p: 0,
                              borderRadius: 1,
                              position: 'relative',
                              ...(disabled
                                ? {
                                  bgcolor: 'grey.50',
                                  color: 'grey.300',
                                }
                                : dateSelected
                                  ? {
                                    bgcolor: '#EDE7F6',
                                    color: '#7A6FA1',
                                    border: '2px solid',
                                    borderColor: '#7A6FA1',
                                    fontWeight: 600,
                                    '&:hover': {
                                      bgcolor: '#D1C4E9',
                                    },
                                  }
                                  : isActive
                                    ? {
                                      bgcolor: '#7A6FA1',
                                      color: 'white',
                                      '&:hover': {
                                        bgcolor: '#7A6FA1',
                                      },
                                    }
                                    : {
                                      bgcolor: 'white',
                                      color: 'text.primary',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      '&:hover': {
                                        bgcolor: 'grey.50',
                                        borderColor: '#7A6FA1',
                                      },
                                    }),
                            }}
                          >
                            {d.getDate()}
                            {dateSelected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 2,
                                  right: 2,
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: '#7A6FA1',
                                }}
                              />
                            )}
                          </Button>
                        </Grid>
                      )
                    })}
                  </Grid>
                </Paper>
              </Grid>

              {/* Time Slots */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      Select slot time *
                    </Typography>
                    {selectedDate && (
                      <Chip
                        label={formatDateDisplayLong(selectedDate)}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {!selectedDate ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      py={6}
                    >
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Select a date first
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Choose a date from the calendar to view available time
                        slots
                      </Typography>
                    </Box>
                  ) : loadingSlots ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      py={6}
                    >
                      <CircularProgress size={24} sx={{ color: '#7A6FA1' }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 2 }}
                      >
                        Loading slots...
                      </Typography>
                    </Box>
                  ) : slotsForDate.length === 0 ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      py={6}
                    >
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        No slots available
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Try selecting a different date
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        maxHeight: 300,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      {slotsForDate.map(slot => {
                        const label = `${slot.startTime} - ${slot.endTime}`
                        const dateKey = selectedDate
                          ? formatDateKey(selectedDate)
                          : ''
                        const isSelected =
                          selectedSlots[dateKey] &&
                          selectedSlots[dateKey].startTime === slot.startTime &&
                          selectedSlots[dateKey].endTime === slot.endTime
                        const isFirstSessionDisabled = hasNoSessions && Object.keys(selectedSlots).length > 0;
                        return (
                          <Button
                            key={slot._id ?? label}
                            onClick={() => {
                              if (isFirstSessionDisabled) {
                                showSnackbar("For the first session, you can select only one date.", "error")
                                return;
                              }
                              selectedDate &&
                                handleSelectSlot(selectedDate, slot)
                            }}
                            variant={isSelected ? 'contained' : 'outlined'}
                            fullWidth
                            sx={{
                              textTransform: 'none',
                              ...(isSelected
                                ? {
                                  bgcolor: '#7A6FA1',
                                  '&:hover': {
                                    bgcolor: '#7A6FA1',
                                  },
                                }
                                : {
                                  borderColor: 'divider',
                                  '&:hover': {
                                    borderColor: '#7A6FA1',
                                    bgcolor: 'grey.50',
                                  },
                                }),
                            }}
                          // disabled={isFirstSessionDisabled}
                          >
                            {label}
                          </Button>
                        )
                      })}
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Selected Sessions List */}
            <Box sx={{ mt: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Selected Sessions
                  </Typography>
                  {!hasNoSessions && (
                    <Typography variant="caption" color="text.secondary">
                      {Object.keys(selectedSlots).length} / 10 selected
                    </Typography>
                  )}
                </Box>

                {Object.keys(selectedSlots).length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    py={2}
                  >
                    No sessions selected yet. Select dates and time slots above.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    {Object.entries(selectedSlots)
                      .sort(([dateKeyA], [dateKeyB]) =>
                        dateKeyA.localeCompare(dateKeyB)
                      )
                      .map(([dateKey, slot]) => {
                        const parts = dateKey.split('-').map(Number)
                        const dateObj = new Date(
                          parts[0],
                          parts[1] - 1,
                          parts[2]
                        )
                        const dayLabel = formatDateDisplayLong(dateObj)

                        return (
                          <Box
                            key={dateKey}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 1.5,
                              bgcolor: 'white',
                              border: '1px solid',
                              borderColor: 'success.light',
                              borderRadius: 1,
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {dayLabel}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {slot.startTime} – {slot.endTime}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => removeSelectedSlot(dateKey)}
                              sx={{ color: 'error.main' }}
                            >
                              <FiX />
                            </IconButton>
                          </Box>
                        )
                      })}
                  </Box>
                )}
              </Paper>
            </Box>

            {/* Payment Method Selection */}
            <Box sx={{ mt: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'white',
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Payment Method{' '}
                    <Typography component="span" color="error.main">
                      *
                    </Typography>
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={paymentMethod}
                  exclusive
                  onChange={(_, value) => {
                    if (hasNoSessions && value === 'insurance') {
                      showSnackbar("Insurance is not allowed for the first session.", "error")
                      return;
                    }
                    if (value !== null) {
                      setPaymentMethod(value)
                    }
                  }}
                  aria-label="payment method"
                  fullWidth
                  sx={{
                    gap: 2,
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      py: 1.5,
                      px: 3,
                      border: '2px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      fontWeight: 500,
                      '&.Mui-selected': {
                        backgroundColor: '#7A6FA1',
                        color: 'white',
                        borderColor: '#7A6FA1',
                        '&:hover': {
                          backgroundColor: '#7A6FA1',
                          borderColor: '#7A6FA1',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'grey.50',
                        // borderColor: '#2E7DAF',
                      },
                    },
                  }}
                >
                  <ToggleButton
                    value="out_of_pocket"
                    aria-label="out of pocket"
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CreditCardIcon fontSize="small" />
                      <span>Self Pay (out of pocket)</span>
                    </Box>
                  </ToggleButton>

                  <ToggleButton value="insurance" aria-label="insurance">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalanceIcon fontSize="small" />
                      <span>Insurance Billed</span>
                    </Box>
                  </ToggleButton>
                </ToggleButtonGroup>
                {!paymentMethod && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Please select a payment method
                  </Typography>
                )}
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setAddSessionDialogOpen(false)
              setSelectedSlots({})
              setSelectedDate(today)
              setPaymentMethod('')
            }}
            disabled={isSubmittingSession}
          >
            Cancel
          </Button>
          <Button
            onClick={() => setAreYouSureDialogOpen(true)}
            variant="contained"
            disabled={
              isSubmittingSession ||
              Object.keys(selectedSlots).length === 0 ||
              !paymentMethod
            }
            sx={{
              backgroundColor: '#7A6FA1',
              '&:hover': {
                backgroundColor: '#7A6FA1',
              },
            }}

          >

            {`Add ${Object.keys(selectedSlots).length} Session${Object.keys(selectedSlots).length > 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Note Dialog (using NoteEditor) - Already handled by existing NoteEditor */}
      <AreYouSurePopup
        open={areYouSureDialogOpen}
        onClose={() => setAreYouSureDialogOpen(false)}
        onConfirm={handleAddSessionSubmit}
        message={
          paymentMethod === 'out_of_pocket'
            ? 'Do you want to proceed with adding this session as an out-of-pocket payment?'
            : paymentMethod === 'insurance'
              ? 'Do you want to proceed with adding this session as an insurance session?'
              : 'Are you sure you want to add this session?'
        }
        confirmText="Yes"
        cancelText="No"
        isProcessing={isSubmittingSession}
        color="primary"
      // type="success"
      />
      {/* Reschedule Session Dialog */}
      <Dialog
        open={rescheduleDialogOpen}
        onClose={() =>
          !isSubmittingReschedule && setRescheduleDialogOpen(false)
        }
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Reschedule Session</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            {selectedSessionForReschedule && (
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: 'warning.lighter',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'warning.light',
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                  Current Session
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(
                    new Date(selectedSessionForReschedule.scheduledAt),
                    'MMMM dd, yyyy h:mm a'
                  )}
                </Typography>
              </Box>
            )}

            {/* Calendar and Time Slots Side by Side */}
            <Grid container spacing={3}>
              {/* Calendar */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => setRescheduleMonthOffset(m => m - 1)}
                      >
                        <FiChevronLeft />
                      </IconButton>
                      <Typography variant="body1" fontWeight={600}>
                        {rescheduleMonthStart.toLocaleString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        })}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setRescheduleMonthOffset(m => m + 1)}
                      >
                        <FiChevronRight />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Select a day *
                    </Typography>
                  </Box>

                  <Grid container spacing={0.5} sx={{ mb: 2 }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                      d => (
                        <Grid item xs={12 / 7} key={d}>
                          <Typography
                            variant="caption"
                            textAlign="center"
                            display="block"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            {d}
                          </Typography>
                        </Grid>
                      )
                    )}
                  </Grid>

                  <Grid container spacing={0.5}>
                    {rescheduleMonthDates.map((d, index) => {
                      if (d === null) {
                        return (
                          <Grid item xs={12 / 7} key={`empty-${index}`}>
                            <Box sx={{ aspectRatio: '1' }} />
                          </Grid>
                        )
                      }

                      const key = formatDateKey(d)
                      const disabled = isPastDate(d)
                      const isActive =
                        rescheduleDate && formatDateKey(rescheduleDate) === key

                      return (
                        <Grid item xs={12 / 7} key={key}>
                          <Button
                            onClick={() => !disabled && setRescheduleDate(d)}
                            disabled={disabled}
                            fullWidth
                            sx={{
                              aspectRatio: '1',
                              minWidth: 0,
                              p: 0,
                              borderRadius: 1,
                              ...(disabled
                                ? {
                                  bgcolor: 'grey.50',
                                  color: 'grey.300',
                                }
                                : isActive
                                  ? {
                                    bgcolor: '#7A6FA1',
                                    color: 'white',
                                    '&:hover': {
                                      bgcolor: '#7A6FA1',
                                    },
                                  }
                                  : {
                                    bgcolor: 'white',
                                    color: '#7A6FA1',
                                    border: '1px solid',
                                    borderColor: '#7A6FA1',
                                    '&:hover': {
                                      bgcolor: 'grey.50',
                                      borderColor: '#7A6FA1',
                                    },
                                  }),
                            }}
                          >
                            {d.getDate()}
                          </Button>
                        </Grid>
                      )
                    })}
                  </Grid>
                </Paper>
              </Grid>

              {/* Time Slots */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      Select new slot time *
                    </Typography>
                    {rescheduleDate && (
                      <Chip
                        label={formatDateDisplayLong(rescheduleDate)}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {!rescheduleDate ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      py={6}
                    >
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Select a date first
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Choose a date from the calendar to view available time
                        slots
                      </Typography>
                    </Box>
                  ) : loadingRescheduleSlots ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      py={6}
                    >
                      <CircularProgress size={24} sx={{ color: '#7A6FA1' }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 2 }}
                      >
                        Loading slots...
                      </Typography>
                    </Box>
                  ) : rescheduleSlots.length === 0 ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      py={6}
                    >
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        No slots available
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Try selecting a different date
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        maxHeight: 300,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      {rescheduleSlots.map(slot => {
                        const label = `${slot.startTime} - ${slot.endTime}`
                        const isSelected =
                          selectedRescheduleSlot &&
                          selectedRescheduleSlot.startTime === slot.startTime &&
                          selectedRescheduleSlot.endTime === slot.endTime

                        return (
                          <Button
                            key={slot._id ?? label}
                            onClick={() => setSelectedRescheduleSlot(slot)}
                            variant={isSelected ? 'contained' : 'outlined'}
                            fullWidth
                            sx={{
                              textTransform: 'none',
                              ...(isSelected
                                ? {
                                  bgcolor: '#7A6FA1',
                                  '&:hover': {
                                    bgcolor: '#7A6FA1',
                                  },
                                }
                                : {
                                  borderColor: 'divider',
                                  '&:hover': {
                                    borderColor: '#7A6FA1',
                                    bgcolor: 'grey.50',
                                  },
                                }),
                            }}
                          >
                            {label}
                          </Button>
                        )
                      })}
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setRescheduleDialogOpen(false)}
            disabled={isSubmittingReschedule}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRescheduleSubmit}
            variant="contained"
            disabled={
              isSubmittingReschedule ||
              !rescheduleDate ||
              !selectedRescheduleSlot
            }
            sx={{
              backgroundColor: '#7A6FA1',
              '&:hover': {
                backgroundColor: '#7A6FA1',
              },
            }}
            startIcon={
              isSubmittingReschedule ? <CircularProgress size={16} /> : null
            }
          >
            {isSubmittingReschedule ? 'Rescheduling...' : 'Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Session Dialog */}
      <Dialog
        open={cancelSessionDialogOpen}
        onClose={() => !isSubmittingCancel && setCancelSessionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Session</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            {selectedSessionForCancel && (
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                  Session Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(
                    new Date(selectedSessionForCancel.scheduledAt),
                    'MMMM dd, yyyy h:mm a'
                  )}
                </Typography>
              </Box>
            )}

            <TextField
              label="Reason for Cancellation *"
              multiline
              rows={4}
              fullWidth
              value={cancelSessionReason}
              onChange={e => setCancelSessionReason(e.target.value)}
              placeholder="Please provide a reason for cancelling this session..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setCancelSessionDialogOpen(false)}
            disabled={isSubmittingCancel}
          >
            Close
          </Button>
          <Button
            onClick={handleCancelSubmit}
            variant="contained"
            color="error"
            disabled={isSubmittingCancel || !cancelSessionReason.trim()}
            startIcon={
              isSubmittingCancel ? <CircularProgress size={16} /> : null
            }
          >
            {isSubmittingCancel ? 'Cancelling...' : 'Cancel Session'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat Dialog */}
      {contract.patient.id && contract.therapist.id && (
        <ChatDialog
          open={chatDialogOpen}
          onClose={() => setChatDialogOpen(false)}
          patientId={contract.patient.id}
          therapistId={contract.therapist.id}
          patientDisplayName={contract.patient.name}
          patientAvatarUrl={contract.patient.avatar}
        />
      )}

      {/* PoC Generation Dialog */}
      {selectedSessionForPoC && (
        <PoCGenerationDialog
          open={pocDialogOpen}
          onClose={() => {
            setPocDialogOpen(false)
            setSelectedSessionForPoC(null)
            setEditingPoC(null)
          }}
          session={selectedSessionForPoC}
          contractId={contract.id}
          contract={contract}
          onSuccess={handlePoCSuccess}
          onError={message => {
            showSnackbar(message, 'error')
            setPocDialogOpen(false)
            setSelectedSessionForPoC(null)
            setEditingPoC(null)
          }}
          existingPoC={editingPoC}
        />
      )}

      {/* PoC Documents Dialog */}
      <PoCDocumentsDialog
        open={pocDocumentsDialogOpen}
        onClose={() => setPocDocumentsDialogOpen(false)}
        documents={pocDocuments}
        loading={pocDocumentsLoading}
        onEdit={documentId => {
          const doc = pocDocuments.find(d => d.id === documentId)
          if (doc && (doc as any).apiDocument) {
            const apiDoc = (doc as any).apiDocument as ApiPoCDocument & {
              session_id?: string | { _id?: string; id?: string }
            }
            // Extract session ID - handle both object and string cases
            let sessionId: string | undefined
            if (typeof apiDoc.session_id === 'string') {
              sessionId = apiDoc.session_id
            } else if (
              apiDoc.session_id &&
              typeof apiDoc.session_id === 'object' &&
              apiDoc.session_id !== null
            ) {
              const sessionIdObj = apiDoc.session_id as {
                _id?: string
                id?: string
              }
              sessionId = sessionIdObj._id || sessionIdObj.id
            }

            if (!sessionId) {
              showSnackbar('Session ID not found in PoC document', 'error')
              return
            }

            // Find the session for this PoC
            const session = contract.sessions.find(
              s => (s._id || s.id) === sessionId
            )
            if (session) {
              setEditingPoC(apiDoc as ApiPoCDocument)
              setSelectedSessionForPoC(session)
              setPocDialogOpen(true)
              setPocDocumentsDialogOpen(false)
            } else {
              showSnackbar('Session not found for this PoC', 'error')
            }
          }
        }}
        onView={documentId => {
          const doc = pocDocuments.find(d => d.id === documentId)
          if (doc && (doc as any).apiDocument) {
            setSelectedPoCForView((doc as any).apiDocument)
            setPocDetailModalOpen(true)
          }
        }}
        onDownload={documentId => {
          console.log('Download PoC document:', documentId)
          showSnackbar('Downloading PoC document...', 'info')
          // TODO: Implement download functionality
        }}
        onDownloadPreauth={documentId => {
          showSnackbar(
            'Preauthorization documents download will be available soon',
            'info'
          )
        }}
        onSubmitForPreauth={handleSubmitForPreauth}
        onPreauthCompleted={handlePreauthCompleted}
        onPreauthDeclined={handlePreauthDeclined}
      />

      {/* PoC Detail Modal */}
      <PoCDetailModal
        open={pocDetailModalOpen}
        onClose={() => {
          setPocDetailModalOpen(false)
          setSelectedPoCForView(null)
        }}
        document={selectedPoCForView}
      />

      {/* Preauthorization Submission Dialog */}
      <PreauthSubmissionDialog
        open={preauthSubmissionDialogOpen}
        onClose={() => {
          setPreauthSubmissionDialogOpen(false)
          setSelectedPoCForPreauth(null)
        }}
        onSubmit={handlePreauthSubmission}
        loading={preauthLoading}
      />

      {/* Preauthorization Completed Dialog */}
      <PreauthCompletedDialog
        open={preauthCompletedDialogOpen}
        onClose={() => {
          setPreauthCompletedDialogOpen(false)
          setSelectedPoCForPreauth(null)
        }}
        onSubmit={handlePreauthCompletedSubmit}
        loading={preauthLoading}
      />

      {/* Preauthorization Declined Dialog */}
      <PreauthDeclinedDialog
        open={preauthDeclinedDialogOpen}
        onClose={() => {
          setPreauthDeclinedDialogOpen(false)
          setSelectedPoCForPreauth(null)
        }}
        onSubmit={handlePreauthDeclinedSubmit}
        loading={preauthLoading}
      />

      {/* Complete Contract Confirmation Dialog */}
      <Dialog
        open={completeContractDialogOpen}
        onClose={handleCloseCompleteContractDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 8,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: 'success.light',
              color: 'success.contrastText',
            }}
          >
            <CheckCircleIcon />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            Mark Contract as Completed
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: '1rem !important', pb: 2 }}>
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
              Confirm Contract Completion
            </AlertTitle>
            <Typography variant="body2">
              Are you sure you want to mark this contract as completed? This
              action will change the contract status to "completed" and may
              affect future operations.
            </Typography>
          </Alert>
          <Box
            sx={{
              p: 2,
              backgroundColor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Contract Details:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Contract ID:</strong>{' '}
                {contract.externalId || contract.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Patient:</strong> {contract.patient.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Sessions:</strong> {contract.completedSessions} /{' '}
                {contract.plannedSessions} completed
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            gap: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            onClick={handleCloseCompleteContractDialog}
            variant="outlined"
            color="inherit"
            disabled={completingContract}
            sx={{
              minWidth: 120,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          {/*onloy this button show atlest one contract copmleted*/}
          {contract.completedSessions > 0 && (
            <Button
              onClick={handleCompleteContract}
              color="success"
              variant="contained"
              disabled={completingContract}
              startIcon={
                completingContract ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckCircleIcon />
                )
              }
              sx={{
                minWidth: 160,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              {completingContract ? 'Completing...' : 'Mark as Completed'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Loading dialog while agency conversation is being created */}
      <Dialog
        open={chatDialogOpen && agencyConversationLoading}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Connecting to chat...
          </Typography>
        </Box>
      </Dialog>

      {/* Error dialog if agency conversation creation fails */}
      <Dialog
        open={chatDialogOpen && !agencyConversationLoading && !!agencyConversationError}
        onClose={() => {
          setChatDialogOpen(false)
          setAgencyConversationError(null)
          setMessageTarget(null)
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Unable to start chat</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {agencyConversationError}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setChatDialogOpen(false)
              setAgencyConversationError(null)
              setMessageTarget(null)
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Agency Chat Dialog - patient or provider */}
      {chatDialogOpen && !agencyConversationLoading && !agencyConversationError && agencyConversation && (
        <ChatDialog
          open={chatDialogOpen}
          onClose={() => {
            setChatDialogOpen(false)
            setAgencyConversation(null)
            setMessageTarget(null)
          }}
          patientId={contract.patient.id}
          therapistId={contract.therapist.id}
          patientDisplayName={contract.patient.name}
          therapistDisplayName={contract.therapist.name}
          participantDisplayName={
            messageTarget === 'patient'
              ? contract.patient.name
              : contract.therapist.name
          }
          initialConversation={agencyConversation}
        />
      )}
    </Box>
  )
}

export default ContractDetail
